/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import styles from "./CreateUserModal.module.css";
import { localUsername } from "./localUsername";
import { useState, useEffect } from "react";
import { type MatrixClient } from "matrix-js-sdk";
import { useTranslation } from "react-i18next";
import { InputField } from "../../input/Input";
import { backendFetch } from "../../utils/backend";
import { localizedRequestError } from "../../utils/localizedError";
import { generateOrganizerPassword } from "./password";

export function OrganizerPasswordReset({
  client,
  userId,
}: {
  client: MatrixClient;
  userId: string;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [copyVersion, setCopyVersion] = useState(0);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 5000);
    return () => window.clearTimeout(timer);
  }, [copied, copyVersion]);
  const buttonStyle = { padding: "6px 10px", fontSize: 13, minHeight: 36 };
  if (!open)
    return (
      <button
        type="button"
        className={styles.action}
        style={buttonStyle}
        onClick={() => {
          setPassword(generateOrganizerPassword());
          setOpen(true);
        }}
      >
        {t("instance.ui.change_password")}
      </button>
    );
  return (
    <form
      style={{
        display: "grid",
        gap: 10,
        marginTop: 12,
        flexBasis: "100%",
        minWidth: 0,
      }}
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setError("");
        void backendFetch(client, "/api/admin/organizers/password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, password }),
        })
          .then(() => {
            setDone(true);
            setShown(true);
          })
          .catch((cause) => setError(localizedRequestError(cause, t)))
          .finally(() => setBusy(false));
      }}
    >
      <p style={{ margin: 0, color: "var(--app-muted)", fontSize: 13 }}>
        {t(
          done
            ? "instance.ui.organizer_password_reset"
            : "instance.ui.reset_password_hint",
        )}
      </p>
      <InputField
        id={`reset-password-${userId}`}
        type={shown ? "text" : "password"}
        autoComplete="new-password"
        label={t("instance.ui.new_password")}
        value={password}
        minLength={10}
        maxLength={256}
        required
        readOnly={done}
        disabled={busy}
        onChange={(event) => setPassword(event.target.value)}
      />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {!done && (
          <button
            type="button"
            className={styles.action}
            style={buttonStyle}
            disabled={busy}
            onClick={() => setPassword(generateOrganizerPassword())}
          >
            {t("instance.create_user_modal.generate_password")}
          </button>
        )}
        <button
          type="button"
          className={styles.action}
          style={buttonStyle}
          onClick={() => setShown(!shown)}
        >
          {t(
            shown
              ? "instance.create_user_modal.hide"
              : "instance.create_user_modal.show",
          )}
        </button>
        {done && (
          <button
            type="button"
            className={styles.action}
            style={buttonStyle}
            onClick={() => {
              void navigator.clipboard
                .writeText(`${localUsername(userId)}\n${password}`)
                .then(() => {
                  setCopied(true);
                  setCopyVersion((value) => value + 1);
                })
                .catch(() =>
                  setError(
                    t(
                      "instance.create_user_modal.could_not_copy_select_and_copy_the_credentials_manually",
                    ),
                  ),
                );
            }}
          >
            <span className={styles.copyText} data-copied={copied}>
              <span aria-hidden={copied}>
                {t("instance.create_user_modal.copy_login_and_password")}
              </span>
              <span aria-hidden={!copied}>
                {t("instance.create_user_modal.copied")}
              </span>
            </span>
          </button>
        )}
        {!done && (
          <button
            type="submit"
            className={styles.action}
            style={buttonStyle}
            disabled={busy}
          >
            {t("instance.ui.reset_password")}
          </button>
        )}
        <button
          type="button"
          className={styles.action}
          style={buttonStyle}
          disabled={busy}
          onClick={() => {
            setCopied(false);
            setPassword("");
            setOpen(false);
            setDone(false);
            setShown(false);
            setError("");
          }}
        >
          {t("action.close")}
        </button>
      </div>
      {error && <p role="alert">{error}</p>}
    </form>
  );
}
